import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import useAuth from '../hooks/useAuth';
import api from '../services/api';
import { IS_MOCK_MODE } from '../services/mockData';
import {
  listAllStudents,
  getStudentCreditStatus,
  setPLACredits,
  setCreditStartDate,
  generateTranscriptPdf,
} from '../services/collegeApi';
import { formatDate, formatDateTime } from '../utils/formatters';
import ProgressMeter from '../components/common/ProgressMeter';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { ROLES } from '../utils/constants';

/**
 * Fetch credit progress for an enrollment.
 *
 * The real backend action is `get_credit_status` dispatched via
 * `enrollment_lookup` scope, which requires `enrollment_id` in the body.
 * collegeApi.getStudentCreditStatus sends `student_id` (works for mock
 * mode only).  In real mode we call the API directly with the correct
 * field name.
 */
const fetchCreditProgress = (enrollmentId) => {
  if (IS_MOCK_MODE) {
    // Mock resolves by student_id — enrollment_id won't match, so pass
    // the student_id portion.  In real mode this path is never hit.
    return getStudentCreditStatus(enrollmentId);
  }
  return api
    .post('/college/get_credit_status', {
      action: 'get_credit_status',
      enrollment_id: enrollmentId,
    })
    .then((res) => res.data);
};

/**
 * Normalize the backend response (nested) vs mock response (flat) into a
 * single shape the CreditDetail component can consume.
 */
const normalizeCreditResponse = (raw) => {
  if (!raw) return null;

  // Real backend returns: {enrollment: {...}, progress: {...},
  // requirements: {...}, activity: [...], ...}
  if (raw.enrollment) {
    const e = raw.enrollment;
    return {
      enrollment_id: e.enrollment_id,
      student_name: e.student_name,
      student_email: e.student_email,
      cohort_id: e.cohort_id,
      cohort_name: e.cohort_name,
      course_code: e.course_code,
      credit_status: e.status,
      enrolled_at: e.enrolled_at,
      credit_start_date: e.credit_start_date,
      pla_credits: e.pla_credits,
      pla_notes: e.pla_notes,
      prior_work_accepted: e.prior_work_accepted,
      requirements: raw.requirements || {},
      progress: raw.progress || {},
      assessments: raw.activity || [],
      last_calculated_at: raw.last_calculated_at,
      recent_settings_change: raw.recent_settings_change || null,
      mentor_feedback_excerpt: raw.mentor_feedback_excerpt || null,
    };
  }

  // Mock mode returns flat shape — pass through as-is
  return raw;
};

export default function CreditTrackerPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentParam = searchParams.get('student');

  const [students, setStudents] = useState([]);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(studentParam || null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [editPLA, setEditPLA] = useState(false);
  const [editStartDate, setEditStartDate] = useState(false);
  const [downloadingTranscript, setDownloadingTranscript] = useState(false);

  const canEdit = role === ROLES.ADMIN;

  useEffect(() => {
    if (!user?.college_id) return;
    setLoadingList(true);
    listAllStudents(user.college_id)
      .then((d) => setStudents(d?.items || d?.students || []))
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoadingList(false));
  }, [user]);

  useEffect(() => {
    if (!selectedEnrollmentId) {
      setCreditStatus(null);
      return;
    }
    setLoadingDetail(true);
    fetchCreditProgress(selectedEnrollmentId)
      .then((raw) => setCreditStatus(normalizeCreditResponse(raw)))
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoadingDetail(false));
  }, [selectedEnrollmentId]);

  const handleStudentChange = (eid) => {
    setSelectedEnrollmentId(eid);
    if (eid) {
      setSearchParams({ student: eid });
    } else {
      setSearchParams({});
    }
  };

  const handleSavePLA = async (credits) => {
    try {
      await setPLACredits(selectedEnrollmentId, creditStatus.cohort_id, credits);
      setSnack('PLA credits updated');
      setEditPLA(false);
      const fresh = await fetchCreditProgress(selectedEnrollmentId);
      setCreditStatus(normalizeCreditResponse(fresh));
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const handleSaveStartDate = async (date) => {
    try {
      await setCreditStartDate(selectedEnrollmentId, creditStatus.cohort_id, date);
      setSnack('Credit start date updated');
      setEditStartDate(false);
      const fresh = await fetchCreditProgress(selectedEnrollmentId);
      setCreditStatus(normalizeCreditResponse(fresh));
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const handleDownloadTranscript = async () => {
    if (!creditStatus) return;
    setDownloadingTranscript(true);
    try {
      const result = await generateTranscriptPdf(user.college_id, selectedEnrollmentId, creditStatus.cohort_id);
      const downloadUrl = result?.url || result?.report_url;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener');
      } else {
        setSnack('Transcript queued. Check Reports for download.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setDownloadingTranscript(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Credit Tracker</Typography>
          <Typography variant="body1" color="text.secondary">
            Hours, assessments, and mentor approvals against cohort requirements.
          </Typography>
        </Box>
        {selectedEnrollmentId && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => handleStudentChange(null)}
            variant="outlined"
          >
            Pick another student
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          options={students}
          getOptionLabel={(s) => s?.name || s?.email || ''}
          isOptionEqualToValue={(o, v) => (o.enrollment_id || o.student_id) === (v.enrollment_id || v.student_id)}
          loading={loadingList}
          value={students.find((s) => (s.enrollment_id || s.student_id) === selectedEnrollmentId) || null}
          onChange={(_e, val) => handleStudentChange(val?.enrollment_id || val?.student_id || null)}
          renderInput={(params) => (
            <TextField {...params} label="Select student" placeholder="Search by name or email" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.enrollment_id || option.student_id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: 'primary.light' }}>
                  {(option.name || option.email || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {option.name || option.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.cohort_name || 'No cohort'}
                  </Typography>
                </Box>
                <StatusChip status={option.status} />
              </Box>
            </li>
          )}
        />
      </Paper>

      {!selectedEnrollmentId ? (
        <Paper>
          <EmptyState
            title="Pick a student to view credit detail"
            message="Search by name or email above. You can also click any student row from the Roster."
            action={<Button variant="outlined" onClick={() => navigate('/students')}>Go to Roster</Button>}
          />
        </Paper>
      ) : loadingDetail ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !creditStatus ? (
        <Alert severity="warning">No credit data found for this student.</Alert>
      ) : (
        <CreditDetail
          status={creditStatus}
          canEdit={canEdit}
          onEditPLA={() => setEditPLA(true)}
          onEditStartDate={() => setEditStartDate(true)}
          onDownloadTranscript={handleDownloadTranscript}
          downloadingTranscript={downloadingTranscript}
        />
      )}

      <PLAEditDialog
        open={editPLA}
        initial={creditStatus?.pla_credits}
        onClose={() => setEditPLA(false)}
        onSave={handleSavePLA}
      />
      <StartDateDialog
        open={editStartDate}
        initial={creditStatus?.credit_start_date}
        onClose={() => setEditStartDate(false)}
        onSave={handleSaveStartDate}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function CreditDetail({ status, canEdit, onEditPLA, onEditStartDate, onDownloadTranscript, downloadingTranscript }) {
  const reqs = status.requirements || {};
  const progress = status.progress || {};
  const hasPLA = (status.pla_credits ?? 0) > 0;
  const change = status.recent_settings_change;

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.light', fontWeight: 700 }}>
              {(status.student_name || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {status.student_name || status.student_email || 'Student'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status.cohort_name || 'No cohort'} {status.course_code ? `· ${status.course_code}` : ''}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <StatusChip status={status.credit_status || 'in_progress'} />
            <Button
              variant="contained"
              startIcon={downloadingTranscript ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={onDownloadTranscript}
              disabled={downloadingTranscript}
            >
              Transcript
            </Button>
          </Stack>
        </Box>
      </Paper>

      {hasPLA && (
        <Alert
          severity="info"
          icon={<StarIcon />}
          action={canEdit && (
            <IconButton color="inherit" size="small" onClick={onEditPLA}><EditIcon fontSize="small" /></IconButton>
          )}
        >
          <Typography variant="body2">
            <strong>Prior Learning Assessment (PLA)</strong> credits applied: <strong>{status.pla_credits}</strong> hours.
            {status.pla_notes ? ` ${status.pla_notes}` : ''}
          </Typography>
        </Alert>
      )}

      {change && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Credit requirements were updated on {formatDate(change.date)} by {change.changed_by}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {change.field_label || change.field}: <strong>{change.old_value}</strong> → <strong>{change.new_value}</strong>.
            This student's progress is calculated against the current requirement.
          </Typography>
        </Alert>
      )}

      {/* Progress meters */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Progress against requirements</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each assessment includes a mandatory mentor review.
        </Typography>
        <Stack spacing={2.5}>
          <ProgressMeter
            label="Hours logged"
            current={progress.hours_logged || 0}
            required={reqs.hours || 0}
            unit="hrs"
          />
          <ProgressMeter
            label="Assessments completed & approved"
            current={progress.assessments_completed || 0}
            required={reqs.assessments || 0}
          />
        </Stack>
      </Paper>

      {/* Metadata */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Enrollment details</Typography>
        <Grid container spacing={3}>
          <DetailItem label="Cohort" value={status.cohort_name} />
          <DetailItem label="Course code" value={status.course_code} mono />
          <DetailItem label="Enrolled" value={formatDate(status.enrolled_at)} />
          <DetailItem
            label="Credit start date"
            value={formatDate(status.credit_start_date)}
            action={canEdit && (
              <IconButton size="small" onClick={onEditStartDate}><EditIcon fontSize="small" /></IconButton>
            )}
            hint="Hours before this date don't count toward this cohort's requirements"
          />
          <DetailItem label="PLA credits" value={`${status.pla_credits || 0} hours`} action={canEdit && (
            <IconButton size="small" onClick={onEditPLA}><EditIcon fontSize="small" /></IconButton>
          )} />
          <DetailItem label="Last updated" value={formatDateTime(status.last_calculated_at)} />
        </Grid>
      </Paper>

      {/* Assessment ledger */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Assessment ledger ({(status.assessments || []).length})
        </Typography>
        {(!status.assessments || status.assessments.length === 0) ? (
          <Typography variant="body2" color="text.secondary">No assessments yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {status.assessments.map((a) => (
              <Box
                key={a.assessment_id}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {a.client_name || 'Client redacted'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(a.completed_at || a.time_slot || a.scheduled_time)}
                    {a.service_type ? ` · ${a.service_type}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`${a.hours_credited || a.hours || 1}h`} size="small" variant="outlined" />
                  {a.mentor_name && <Chip label={`Mentor: ${a.mentor_name}`} size="small" variant="outlined" />}
                  {!a.mentor_name && a.mentor_approved && <Chip label="Mentor approved" size="small" color="success" variant="outlined" />}
                  <StatusChip status={a.status === 'completed' ? 'qualified' : a.status} />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Mentor feedback excerpt */}
      {status.mentor_feedback_excerpt && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Mentor feedback excerpt</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
            "{status.mentor_feedback_excerpt}"
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}

function DetailItem({ label, value, mono, action, hint }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          {action}
        </Box>
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, mt: 0.25, fontFamily: mono ? 'monospace' : undefined }}
        >
          {value || '—'}
        </Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </Box>
    </Grid>
  );
}

function PLAEditDialog({ open, initial, onClose, onSave }) {
  const [credits, setCredits] = useState(initial ?? 0);
  const [notes, setNotes] = useState('');
  useEffect(() => { setCredits(initial ?? 0); }, [initial, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set PLA Credits</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Prior Learning Assessment hours applied to this cohort. These count toward the hours requirement.
          </Typography>
          <TextField
            label="Hours"
            type="number"
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
            autoFocus
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="e.g. CompTIA Security+ certification, prior coursework"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(Number(credits))}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function StartDateDialog({ open, initial, onClose, onSave }) {
  const [date, setDate] = useState(initial || '');
  useEffect(() => { setDate(initial || ''); }, [initial, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set Credit Start Date</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Hours logged before this date won't count toward the cohort's requirements. Use for late enrollments
            or to reset the clock for a re-enrolled student.
          </Typography>
          <TextField
            label="Credit start date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(date)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
